import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Map, Overlay, ZoomControl } from 'pigeon-maps';
import { Selection } from '../selection';
import PositionStore from '../../../stores/position-store';
import { ILocation } from '../../../types/location';
import { Box, CircularProgress, Tooltip, Typography, useTheme } from '@mui/material';

interface Props {
    selected: Selection;
    onSelect: (sel: Selection) => void;
}

const store = new PositionStore();

function darkTiles(x: number, y: number, z: number, dpr?: number): string {
    const r = dpr && dpr >= 2 ? '@2x' : '';
    const s = ['a', 'b', 'c', 'd'][(x + y) % 4];
    return `https://${s}.basemaps.cartocdn.com/dark_all/${z}/${x}/${y}${r}.png`;
}

function MapPin({ item, onSelect }: { item: ILocation; onSelect: (sel: Selection) => void }) {
    const theme = useTheme();
    const color = theme.palette.primary.main;

    const handleClick = () => {
        if (item.node_id !== null) {
            onSelect({ kind: 'location', locationId: item.location_id, nodeId: item.node_id });
        }
    };

    const title = (
        <Box>
            <Typography variant="caption" display="block" fontWeight="bold">
                {item.name}
            </Typography>
            {item.description && (
                <Typography variant="caption" display="block">
                    {item.description}
                </Typography>
            )}
            {item.address && (
                <Typography variant="caption" display="block">
                    {item.address}
                </Typography>
            )}
        </Box>
    );

    return (
        <Tooltip title={title} arrow placement="top">
            <Box onClick={handleClick} sx={{ transform: 'translate(-50%, -100%)', cursor: 'pointer', lineHeight: 0 }}>
                <svg width="20" height="28" viewBox="0 0 20 28">
                    <circle cx="10" cy="10" r="10" fill={color} />
                    <polygon points="5,18 15,18 10,28" fill={color} />
                </svg>
            </Box>
        </Tooltip>
    );
}

function computeView(items: ILocation[]): { center: [number, number]; zoom: number } {
    if (items.length === 0) return { center: [55.75, 37.62], zoom: 10 };

    const latitudes = items.map((i) => i.coordinates.x);
    const longitudes = items.map((i) => i.coordinates.y);
    const minLat = Math.min(...latitudes),
        maxLat = Math.max(...latitudes);
    const minLon = Math.min(...longitudes),
        maxLon = Math.max(...longitudes);

    const center: [number, number] = [(minLat + maxLat) / 2, (minLon + maxLon) / 2];

    const span = Math.max(maxLat - minLat, maxLon - minLon);
    const zoom = span === 0 ? 14 : Math.min(Math.max(Math.floor(Math.log2(360 / span)) - 1, 1), 18);

    return { center, zoom };
}

export default observer(function MapLayout({ selected, onSelect }: Props) {
    const theme = useTheme();

    useEffect(() => {
        store.load(selected);
    }, [selected]);

    const { center, zoom } = computeView(store.items);

    return (
        <Box
            sx={{
                width: '100%',
                height: 350,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                m: 1,
                ...(theme.palette.mode === 'dark' && {
                    '& .pigeon-tiles': {
                        filter: 'sepia(0.8) hue-rotate(215deg) saturate(3) brightness(1.5)',
                    },
                }),
            }}
        >
            {store.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Map
                    animate
                    key={JSON.stringify(selected)}
                    defaultCenter={center}
                    defaultZoom={zoom}
                    provider={theme.palette.mode === 'dark' ? darkTiles : undefined}
                >
                    <ZoomControl style={{ right: '10px', top: '10px', left: 'auto' }} />
                    {store.items.map((item) => (
                        <Overlay key={item.location_id} anchor={[item.coordinates.x, item.coordinates.y]}>
                            <MapPin item={item} onSelect={onSelect} />
                        </Overlay>
                    ))}
                </Map>
            )}
        </Box>
    );
});
